import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { generateB2BApiKey, generateB2BInvoice, getB2BInvoices, getB2BApiLogs, getMyProfile, getB2BOrders, submitBulkOrders, requestB2BAccess, setB2BWebhook, payB2BInvoice, createRecurringOrder, getRecurringOrders, updateRecurringOrder, placeOrder } from "../../api/client";
import BottomNav from "../../components/BottomNav";
import UserPageHeader from "../../components/UserPageHeader";
import AddressAutocomplete from "../../components/AddressAutocomplete";
import Collapsible from "../../components/Collapsible";
import ApplicationStatusCard from "../../components/ApplicationStatusCard";
import LoadingIndicator from "../../components/LoadingIndicator";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { PACKAGE_TYPE_LABELS, PACKAGE_TYPES_BY_VEHICLE } from "../../utils/packageLabels";

const WEEKDAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const INVOICE_STATUS_BADGE = {
  unpaid:   { cls: "warning", label: "UNPAID" },
  paid:     { cls: "info",    label: "PAID" },
  overdue:  { cls: "danger",  label: "OVERDUE" },
  combined: { cls: "",        label: "COMBINED" },
};

const ORDER_STATUS_BADGE = {
  requested:          "warning",
  accepted:           "info",
  arrived_at_pickup:  "info",
  picked_up:          "info",
  in_transit:         "info",
  delivered:          "",
  completed:          "",
  cancelled:          "danger",
  failed:             "danger",
  scheduled:          "warning",
};

const ORDER_ACTIVE_STATUSES = ["requested", "accepted", "arrived_at_pickup", "picked_up", "in_transit"];

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:4000";

// Collapsible section — used for the API reference and Orders Initiated so
// they don't compete with the credentials/webhook/bulk-order forms for
// space by default.
function CodeBlock({ children }) {
  return (
    <pre style={{ background: "rgba(0,0,0,0.3)", border: "1px solid var(--border)", borderRadius: "8px", padding: "12px 16px", fontFamily: "monospace", fontSize: "12px", overflowX: "auto", whiteSpace: "pre", color: "var(--text-secondary)" }}>
      {children}
    </pre>
  );
}

export default function B2BDashboard() {
  const location = useLocation();
  // Which application type the "Businesses" landing page linked here as —
  // drives both the apply form's submission and its copy. Defaults to "b2b"
  // when opened directly (e.g. a bookmark) rather than via that page.
  const requestedAccountType = new URLSearchParams(location.search).get("type") === "corporate" ? "corporate" : "b2b";
  const [profile, setProfile] = useState(null);
  const [apiKey, setApiKey] = useState("");
  const [showApiKey, setShowApiKey] = useState(false);
  const [apiKeyCopied, setApiKeyCopied] = useState(false);
  const [companyName, setCompanyName] = useState("");
  const [billingTerms, setBillingTerms] = useState("per_order");
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [requestCompany, setRequestCompany] = useState("");
  const [requestCac, setRequestCac] = useState("");
  const [requestAddress, setRequestAddress] = useState("");
  const [requestEmail, setRequestEmail] = useState("");
  const [requestWebsite, setRequestWebsite] = useState("");
  const [requestTerms, setRequestTerms] = useState("per_order");
  const [requestBusy, setRequestBusy] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState("");
  const [webhookBusy, setWebhookBusy] = useState(false);
  const [billingMode, setBillingMode] = useState("pay_now");

  // Invoice pay / view state
  const [expandedInvoiceId, setExpandedInvoiceId] = useState(null);
  const [payMethodMap, setPayMethodMap] = useState({});
  const [payBusyId, setPayBusyId] = useState(null);

  // Bulk order submission state
  const [bulkInput, setBulkInput] = useState(`[
  {
    "pickup": { "address": "Ikeja, Lagos", "lat": 6.6018, "lng": 3.3514 },
    "dropoff": { "address": "Lekki Phase 1, Lagos", "lat": 6.4474, "lng": 3.4723 },
    "packageType": "bike_under_5kg",
    "packageCategory": "food_consumables",
    "vehicleType": "bike"
  }
]`);
  const [bulkResults, setBulkResults] = useState(null);

  // API request log — tracks every request made with this account's API key.
  const [apiLogs, setApiLogs] = useState([]);

  // Orders this account has initiated — via the bulk API or otherwise —
  // with their live current status. Tracked only here, not on the regular
  // customer "My Orders" page (see GET /users/me/orders' channel filter).
  const [myOrders, setMyOrders] = useState([]);
  const [orderFilter, setOrderFilter] = useState("all"); // "all" | "active" | "completed" | "cancelled"
  const [orderSearch, setOrderSearch] = useState("");

  const { updateAccount } = useAuth();
  const { toastSuccess, toastError, toastInfo } = useToast();
  const [recurringTemplates, setRecurringTemplates] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const [pRes, iRes, oRes] = await Promise.all([getMyProfile(), getB2BInvoices(), getB2BOrders()]);
      setProfile(pRes.data);
      // Keep the auth-context account (which drives BottomNav's "is this an
      // approved corporate account" check) in sync — otherwise a business
      // approved after the user already logged in wouldn't see its nav
      // change until their next login.
      updateAccount(pRes.data);
      if (pRes.data.apiKey) setApiKey(pRes.data.apiKey);
      if (pRes.data.companyName) setCompanyName(pRes.data.companyName);
      if (pRes.data.billingTerms) setBillingTerms(pRes.data.billingTerms);
      if (pRes.data.billingMode) setBillingMode(pRes.data.billingMode);
      if (pRes.data.webhookUrl) setWebhookUrl(pRes.data.webhookUrl);
      setInvoices(iRes.data);
      setMyOrders(oRes.data);
      if (pRes.data.apiKey) {
        try {
          const { data } = await getB2BApiLogs();
          setApiLogs(data);
        } catch { /* non-critical — leave the log empty rather than failing the whole page */ }
      }
      if (pRes.data.corporateApprovalStatus === "approved") {
        try {
          const { data } = await getRecurringOrders();
          setRecurringTemplates(data);
        } catch { /* non-critical — leave the list empty rather than failing the whole page */ }
      }
    } catch {
      setError("Failed to load business account data");
    } finally {
      setLoading(false);
    }
  };

  // ── Corporate booking form — one-off, scheduled, or recurring. Corporate
  // accounts book exclusively here (see PlaceOrder.js's redirect and
  // BottomNav's hidden "Send Package" tab for approved corporate accounts).
  const [bookingMode, setBookingMode] = useState("oneoff"); // "oneoff" | "scheduled" | "recurring"
  const [bookingPickup, setBookingPickup] = useState(null);
  const [bookingDropoff, setBookingDropoff] = useState(null);
  const [bookingKey, setBookingKey] = useState(0); // forces AddressAutocomplete to reset after submit
  const [bookingVehicleType, setBookingVehicleType] = useState("bike");
  const [bookingPackageType, setBookingPackageType] = useState("bike_under_5kg");
  const [bookingPackageDescription, setBookingPackageDescription] = useState("");
  const [bookingScheduledFor, setBookingScheduledFor] = useState("");
  const [bookingFrequency, setBookingFrequency] = useState("daily");
  const [bookingWeekdays, setBookingWeekdays] = useState([]);
  const [bookingTimeOfDay, setBookingTimeOfDay] = useState("09:00");
  const [bookingStartDate, setBookingStartDate] = useState("");
  const [bookingEndDate, setBookingEndDate] = useState("");
  const [bookingBusy, setBookingBusy] = useState(false);
  const [bookingError, setBookingError] = useState("");
  const [bookingMessage, setBookingMessage] = useState("");
  const [recurringBusyId, setRecurringBusyId] = useState(null);

  const resetBookingForm = () => {
    setBookingPickup(null);
    setBookingDropoff(null);
    setBookingKey(k => k + 1);
    setBookingPackageDescription("");
    setBookingScheduledFor("");
  };

  const toggleWeekday = (day) => {
    setBookingWeekdays(days => days.includes(day) ? days.filter(d => d !== day) : [...days, day].sort());
  };

  const handleBookingSubmit = async (e) => {
    e.preventDefault();
    setBookingError("");
    setBookingMessage("");
    if (!bookingPickup || !bookingDropoff) {
      setBookingError("Select both a pickup and drop-off address.");
      toastError("Select both a pickup and drop-off address.");
      return;
    }
    setBookingBusy(true);
    try {
      if (bookingMode === "recurring") {
        if (bookingFrequency === "weekly" && bookingWeekdays.length === 0) {
          throw new Error("Select at least one weekday for a weekly recurring order.");
        }
        if (!bookingStartDate) throw new Error("Select a start date.");
        await createRecurringOrder({
          pickup: bookingPickup,
          dropoff: bookingDropoff,
          vehicleType: bookingVehicleType,
          packageType: bookingPackageType,
          packageDescription: bookingPackageDescription,
          frequency: bookingFrequency,
          weekdays: bookingFrequency === "weekly" ? bookingWeekdays : [],
          timeOfDay: bookingTimeOfDay,
          startDate: bookingStartDate,
          endDate: bookingEndDate || null
        });
        const successText = "✓ Recurring order created — it'll fire automatically on schedule.";
        setBookingMessage(successText);
        toastSuccess(successText);
        const { data } = await getRecurringOrders();
        setRecurringTemplates(data);
      } else {
        if (bookingMode === "scheduled" && !bookingScheduledFor) {
          throw new Error("Select a date and time for the scheduled delivery.");
        }
        const { data } = await placeOrder({
          pickup: bookingPickup,
          dropoff: bookingDropoff,
          vehicleType: bookingVehicleType,
          packageType: bookingPackageType,
          packageDescription: bookingPackageDescription,
          scheduledFor: bookingMode === "scheduled" ? bookingScheduledFor : undefined
        });
        const successText = `✓ Order placed — ₦${data.priceSnapshot?.total?.toLocaleString()}, billed to your consolidated invoice.`;
        setBookingMessage(successText);
        toastSuccess(successText);
        const { data: orders } = await getB2BOrders();
        setMyOrders(orders);
      }
      resetBookingForm();
    } catch (err) {
      const errText = err.response?.data?.error || err.message || "Booking failed.";
      setBookingError(errText);
      // Only toast manually for errors that never reached the API (no
      // err.config = not an axios request, e.g. a client-side validation
      // check above) — anything that did reach the server already got
      // toasted by the axios interceptor in api/client.js, so toasting
      // again here would double it up.
      if (!err.config) toastError(errText);
    } finally {
      setBookingBusy(false);
    }
  };

  const handleToggleRecurringStatus = async (id, status) => {
    setRecurringBusyId(id);
    try {
      await updateRecurringOrder(id, { status });
      toastSuccess(`✓ Recurring order ${status === "active" ? "resumed" : status === "paused" ? "paused" : "cancelled"}.`);
      const { data } = await getRecurringOrders();
      setRecurringTemplates(data);
    } catch (err) {
      // Error already toasted by the axios interceptor (this is a PATCH).
      setBookingError(err.response?.data?.error || "Could not update recurring order.");
    } finally {
      setRecurringBusyId(null);
    }
  };

  useEffect(() => { load(); }, []);

  const handleCopyApiKey = async () => {
    try {
      await navigator.clipboard?.writeText(apiKey);
      setApiKeyCopied(true);
      setTimeout(() => setApiKeyCopied(false), 2000);
    } catch { /* clipboard access denied — silently no-op */ }
  };

  const handleGenerateKey = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");
    setBusy(true);
    try {
      const { data } = await generateB2BApiKey(companyName, billingTerms);
      setApiKey(data.apiKey);
      setShowApiKey(false);
      setMessage("✓ API Key regenerated successfully. Keep it secure!");
      toastSuccess("✓ API Key regenerated successfully. Keep it secure!");
    } catch (err) {
      // Error already toasted by the axios interceptor (this is a POST).
      setError(err.response?.data?.error || "Failed to generate API Key");
    } finally {
      setBusy(false);
    }
  };

  const handleRequestAccess = async (e) => {
    e.preventDefault();
    setError("");
    setRequestBusy(true);
    try {
      await requestB2BAccess({
        companyName: requestCompany,
        cacNumber: requestCac,
        companyAddress: requestAddress,
        officialEmail: requestEmail,
        website: requestWebsite,
        billingTerms: requestTerms,
        accountType: requestedAccountType
      });
      await load();
      // No manual toastSuccess — the backend response includes a `message`
      // field for this endpoint, so the axios interceptor already toasted it.
      setMessage(requestedAccountType === "corporate"
        ? "✓ Corporate access request submitted! An admin will review and approve your account."
        : "✓ B2B access request submitted! An admin will review and approve your account.");
    } catch (err) {
      setError(err.response?.data?.error || "Failed to submit access request.");
    } finally {
      setRequestBusy(false);
    }
  };

  const handleBulkSubmit = async () => {
    setError("");
    setBulkResults(null);
    if (!apiKey) {
      setError("Please generate an API Key first.");
      toastError("Please generate an API Key first.");
      return;
    }
    let ordersArr;
    try {
      ordersArr = JSON.parse(bulkInput);
    } catch {
      setError("Invalid JSON format for bulk orders.");
      toastError("Invalid JSON format for bulk orders.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await submitBulkOrders(ordersArr, apiKey);
      setBulkResults(data);
      toastSuccess(`✓ Batch dispatched — ${data.successful}/${data.totalSubmitted} order(s) created.`);
      try {
        const { data: logs } = await getB2BApiLogs();
        setApiLogs(logs);
      } catch { /* log refresh is best-effort */ }
      try {
        const { data: orders } = await getB2BOrders();
        setMyOrders(orders);
      } catch { /* order list refresh is best-effort */ }
    } catch (err) {
      // Error already toasted by the axios interceptor (this is a POST).
      setError(err.response?.data?.error || "Bulk order dispatch failed.");
    } finally {
      setBusy(false);
    }
  };

  const handleSaveWebhook = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");
    setWebhookBusy(true);
    try {
      await setB2BWebhook(webhookUrl);
      setMessage("✓ Webhook URL saved — your integration will now receive order status callbacks.");
      toastSuccess("✓ Webhook URL saved — your integration will now receive order status callbacks.");
    } catch (err) {
      setError(err.response?.data?.error || "Failed to save webhook URL.");
    } finally {
      setWebhookBusy(false);
    }
  };

  const handleGenerateInvoice = async () => {
    setError("");
    setBusy(true);
    try {
      await generateB2BInvoice();
      await load();
      setMessage("✓ Consolidated invoice generated!");
      toastSuccess("✓ Consolidated invoice generated!");
    } catch (err) {
      setError(err.response?.data?.error || "Invoice generation failed.");
    } finally {
      setBusy(false);
    }
  };

  const handlePayInvoice = async (invoiceId) => {
    const method = payMethodMap[invoiceId] || "wallet";
    setError("");
    setMessage("");
    setPayBusyId(invoiceId);
    try {
      const { data } = await payB2BInvoice(invoiceId, method);
      if (data.checkout?.checkoutUrl) {
        window.open(data.checkout.checkoutUrl, "_blank");
        setMessage("Payment window opened. Complete checkout to settle this invoice.");
        toastInfo("Payment window opened. Complete checkout to settle this invoice.");
      } else {
        setMessage("✓ Invoice paid from wallet!");
        toastSuccess("✓ Invoice paid from wallet!");
        await load();
      }
    } catch (err) {
      setError(err.response?.data?.error || "Invoice payment failed.");
    } finally {
      setPayBusyId(null);
    }
  };

  // Masked unless the credentials field above has been revealed — the docs
  // examples shouldn't be a back door around hiding the key by default.
  const displayApiKey = apiKey
    ? (showApiKey ? apiKey : apiKey.slice(0, 8) + "•".repeat(Math.max(apiKey.length - 8, 0)))
    : "sk_live_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx";

  // B2B/Partner and Corporate are independent tracks — either, both, or
  // neither can be approved on this account at once (mirrors Agent/Fleet
  // Owner elsewhere). Every gate below checks its own track's status, never
  // the other's.
  const b2bStatus = profile?.b2bApprovalStatus || "none";
  const corporateStatus = profile?.corporateApprovalStatus || "none";
  const b2bApproved = b2bStatus === "approved";
  const corporateApproved = corporateStatus === "approved";
  const anyApproved = b2bApproved || corporateApproved;
  // Which track's status the apply/pending/revoked screen (when neither
  // track is approved yet) should reflect — driven by which card on the
  // Businesses landing page linked here.
  const relevantStatus = requestedAccountType === "corporate" ? corporateStatus : b2bStatus;

  const orderActiveCount = myOrders.filter(o => ORDER_ACTIVE_STATUSES.includes(o.status)).length;
  const totalOwed = invoices.filter(inv => inv.status === "unpaid").reduce((sum, inv) => sum + inv.totalAmount, 0);
  const filteredOrders = myOrders
    .filter(o => {
      if (orderFilter === "active") return ORDER_ACTIVE_STATUSES.includes(o.status);
      if (orderFilter === "completed") return ["delivered", "completed"].includes(o.status);
      if (orderFilter === "cancelled") return ["cancelled", "failed"].includes(o.status);
      return true;
    })
    .filter(o => {
      const q = orderSearch.trim().toLowerCase();
      if (!q) return true;
      return (
        o._id.toLowerCase().includes(q) ||
        o.pickup?.address?.toLowerCase().includes(q) ||
        o.dropoff?.address?.toLowerCase().includes(q) ||
        o.rider?.name?.toLowerCase().includes(q)
      );
    });

  return (
    <div className="user-app">
      <BottomNav />
      <div className="user-app-main">
        <UserPageHeader
          backTo="/order"
          backLabel="Send Package"
        />
        <div className="page wide">

      <div className="brand-header">
        <span className="brand-logo">{corporateApproved ? "🏛️" : "🏢"}</span>
        <span className="brand-name">
          {corporateApproved && b2bApproved ? "Business Portal" : corporateApproved ? "Corporate Portal" : "Merchant & B2B Portal"}
        </span>
        <p className="brand-tagline">
          {corporateApproved && b2bApproved
            ? "Corporate booking + consolidated invoices, and B2B/Partner API access — independent of each other"
            : corporateApproved
              ? "Your order list, consolidated invoices, and invoice generation"
              : "Programmatic API key, bulk orders, and consolidated invoicing"}
        </p>
      </div>

      {loading ? (
        <LoadingIndicator label="Loading portal..." />
      ) : !anyApproved && relevantStatus === "none" ? (
        /* ── Request Access State — neither track approved, and the one
            this page was opened for (via the Businesses landing page's
            ?type= link) hasn't been applied for yet. ── */
        <div className="delivery-card" style={{ maxWidth: "520px", margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: "24px" }}>
            <span style={{ fontSize: "48px" }}>{requestedAccountType === "corporate" ? "🏛️" : "🏢"}</span>
            <h3 style={{ marginTop: "8px" }}>Apply for {requestedAccountType === "corporate" ? "Corporate" : "B2B / Partner"} Access</h3>
            <p style={{ color: "var(--text-muted)", fontSize: "14px" }}>
              {requestedAccountType === "corporate"
                ? "Submit your company details. An admin will review and approve your account, then you can place orders through the app and get billed to a consolidated invoice."
                : "Submit your business details. An admin will review and approve your account, then issue your API key."}
            </p>
          </div>
          {error && <p className="error">{error}</p>}
          {message && <p style={{ color: "var(--success)", fontSize: "14px", marginBottom: "8px" }}>{message}</p>}
          <form onSubmit={handleRequestAccess}>
            <div className="form-field">
              <label>Company Name</label>
              <input type="text" placeholder="e.g. Acme Logistics Ltd" value={requestCompany} onChange={e => setRequestCompany(e.target.value)} required />
            </div>
            <div className="form-field">
              <label>CAC Registration Number</label>
              <input type="text" placeholder="e.g. RC1234567" value={requestCac} onChange={e => setRequestCac(e.target.value)} required />
            </div>
            <div className="form-field">
              <label>Company Address</label>
              <textarea rows={2} style={{ resize: "vertical" }} placeholder="Registered business address" value={requestAddress} onChange={e => setRequestAddress(e.target.value)} required />
            </div>
            <div className="form-field">
              <label>Official Email</label>
              <input type="email" placeholder="e.g. ops@acmelogistics.com" value={requestEmail} onChange={e => setRequestEmail(e.target.value)} required />
            </div>
            <div className="form-field">
              <label>Website (optional)</label>
              <input type="url" placeholder="https://acmelogistics.com" value={requestWebsite} onChange={e => setRequestWebsite(e.target.value)} />
            </div>
            <div className="form-field">
              <label>Billing Preference</label>
              <select value={requestTerms} onChange={e => setRequestTerms(e.target.value)} style={{ width: "100%", background: "var(--bg-card)", color: "var(--text-primary)", border: "1px solid var(--border)", borderRadius: "8px", padding: "10px" }}>
                <option value="per_order">Per-Order Payment (Wallet/Card)</option>
                <option value="invoiced">Consolidated Monthly Invoice (Net 14)</option>
              </select>
            </div>
            <button type="submit" disabled={requestBusy} style={{ marginTop: "8px" }}>
              {requestBusy ? "Submitting..." : requestedAccountType === "corporate" ? "📋 Request Corporate Access" : "📋 Request B2B Access"}
            </button>
          </form>
        </div>
      ) : !anyApproved && relevantStatus === "pending" ? (
        <ApplicationStatusCard
          status="pending"
          title="Application Under Review"
          description={`Your ${requestedAccountType === "corporate" ? "Corporate" : "B2B"} access request is being reviewed by our admin team. You'll have access once approved.`}
          footer={
            <div className="goboot-ai-banner">
              <div className="ai-banner-icon">📋</div>
              <div className="ai-banner-text">Company: <strong>{profile.companyName || "—"}</strong> · Billing: <strong>{profile.billingTerms === "invoiced" ? "Monthly Invoice" : "Per Order"}</strong></div>
            </div>
          }
        />
      ) : !anyApproved && relevantStatus === "revoked" ? (
        <ApplicationStatusCard
          status="revoked"
          title={`${requestedAccountType === "corporate" ? "Corporate" : "B2B"} Access Revoked`}
          description="Your access has been revoked by an administrator. Please contact support for more information."
        />
      ) : (
        <>
        {/* Prompt to also apply for the other track, when at least one is
            already approved but this one never was — surfaces the option
            without blocking anything already working. */}
        {corporateApproved && b2bStatus === "none" && (
          <div className="quote-box" style={{ marginBottom: "24px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "8px" }}>
            <span style={{ fontSize: "13px" }}>🔌 Also need programmatic API access? Apply for B2B/Partner too — it won't affect your Corporate access.</span>
            <a href="/b2b?type=b2b" className="secondary" style={{ width: "auto", padding: "6px 14px", fontSize: "12px", borderRadius: "8px", textDecoration: "none", border: "1px solid var(--border)" }}>Apply for B2B</a>
          </div>
        )}
        {b2bApproved && corporateStatus === "none" && (
          <div className="quote-box" style={{ marginBottom: "24px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "8px" }}>
            <span style={{ fontSize: "13px" }}>🏛️ Also want to book through the app with consolidated invoicing? Apply for Corporate too — it won't affect your B2B/Partner access.</span>
            <a href="/b2b?type=corporate" className="secondary" style={{ width: "auto", padding: "6px 14px", fontSize: "12px", borderRadius: "8px", textDecoration: "none", border: "1px solid var(--border)" }}>Apply for Corporate</a>
          </div>
        )}

        {corporateApproved && (
          <div className="delivery-card" style={{ marginBottom: "24px" }}>
            <h3>📦 Book a Delivery</h3>
            <p className="hint" style={{ marginBottom: "12px" }}>
              One-off, scheduled, or a standing recurring order — every booking here is billed to your consolidated invoice below, nothing to pay now.
            </p>

            <div className="role-tabs" style={{ marginBottom: "12px" }}>
              <button type="button" className={`role-tab ${bookingMode === "oneoff" ? "active" : ""}`} onClick={() => setBookingMode("oneoff")}>One-off</button>
              <button type="button" className={`role-tab ${bookingMode === "scheduled" ? "active" : ""}`} onClick={() => setBookingMode("scheduled")}>Scheduled</button>
              <button type="button" className={`role-tab ${bookingMode === "recurring" ? "active" : ""}`} onClick={() => setBookingMode("recurring")}>Recurring</button>
            </div>

            <form onSubmit={handleBookingSubmit}>
              <div className="form-field">
                <label>Pickup Address</label>
                <AddressAutocomplete key={`pickup-${bookingKey}`} placeholder="Search pickup address..." onSelect={setBookingPickup} />
                {bookingPickup && <p className="hint" style={{ marginTop: "4px" }}>✓ {bookingPickup.address}</p>}
              </div>
              <div className="form-field">
                <label>Drop-off Address</label>
                <AddressAutocomplete key={`dropoff-${bookingKey}`} placeholder="Search destination address..." onSelect={setBookingDropoff} />
                {bookingDropoff && <p className="hint" style={{ marginTop: "4px" }}>✓ {bookingDropoff.address}</p>}
              </div>

              <div className="form-field">
                <label>Vehicle Type</label>
                <select
                  value={bookingVehicleType}
                  onChange={e => { setBookingVehicleType(e.target.value); setBookingPackageType(PACKAGE_TYPES_BY_VEHICLE[e.target.value][0]); }}
                >
                  <option value="bike">🏍️ Bike — small/nearby packages may be matched to a bicycle dispatcher</option>
                  <option value="van">🚐 Van</option>
                  <option value="truck">🚚 Truck (quoted on request)</option>
                </select>
              </div>
              <div className="form-field">
                <label>Package Size</label>
                <select value={bookingPackageType} onChange={e => setBookingPackageType(e.target.value)}>
                  {PACKAGE_TYPES_BY_VEHICLE[bookingVehicleType].map((value) => (
                    <option key={value} value={value}>{PACKAGE_TYPE_LABELS[value]}</option>
                  ))}
                </select>
              </div>
              <div className="form-field">
                <label>Package Description (optional)</label>
                <input type="text" placeholder="e.g. 3 boxes of stationery" value={bookingPackageDescription} onChange={e => setBookingPackageDescription(e.target.value)} />
              </div>

              {bookingMode === "scheduled" && (
                <div className="form-field">
                  <label>Delivery Date & Time</label>
                  <input type="datetime-local" value={bookingScheduledFor} onChange={e => setBookingScheduledFor(e.target.value)} min={new Date(Date.now() + 5 * 60 * 1000).toISOString().slice(0, 16)} />
                </div>
              )}

              {bookingMode === "recurring" && (
                <>
                  <div className="form-field">
                    <label>Frequency</label>
                    <select value={bookingFrequency} onChange={e => setBookingFrequency(e.target.value)}>
                      <option value="daily">Every day</option>
                      <option value="weekly">Specific weekdays</option>
                    </select>
                  </div>
                  {bookingFrequency === "weekly" && (
                    <div className="form-field">
                      <label>Weekdays</label>
                      <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                        {WEEKDAY_LABELS.map((label, day) => (
                          <button
                            key={day}
                            type="button"
                            className={bookingWeekdays.includes(day) ? "" : "secondary"}
                            style={{ width: "auto", margin: 0, padding: "6px 12px", fontSize: "13px" }}
                            onClick={() => toggleWeekday(day)}
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="form-field">
                    <label>Time of Day</label>
                    <input type="time" value={bookingTimeOfDay} onChange={e => setBookingTimeOfDay(e.target.value)} />
                  </div>
                  <div className="form-field">
                    <label>Start Date</label>
                    <input type="date" value={bookingStartDate} onChange={e => setBookingStartDate(e.target.value)} min={new Date().toISOString().slice(0, 10)} />
                  </div>
                  <div className="form-field">
                    <label>End Date (optional — leave blank to run indefinitely)</label>
                    <input type="date" value={bookingEndDate} onChange={e => setBookingEndDate(e.target.value)} min={bookingStartDate || new Date().toISOString().slice(0, 10)} />
                  </div>
                </>
              )}

              {bookingError && <p className="error">{bookingError}</p>}
              {bookingMessage && <p style={{ color: "var(--success)", fontSize: "14px", marginBottom: "8px" }}>{bookingMessage}</p>}

              <button type="submit" disabled={bookingBusy}>
                {bookingBusy ? "Submitting..." : bookingMode === "recurring" ? "Create Recurring Order" : "Book Delivery"}
              </button>
            </form>
          </div>
        )}

        {corporateApproved && recurringTemplates.length > 0 && (
          <div className="delivery-card" style={{ marginBottom: "24px" }}>
            <h3>🔁 Recurring Orders</h3>
            {recurringTemplates.map(t => (
              <div key={t._id} className="history-row">
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <strong>{t.pickup.address.slice(0, 26)}... → {t.dropoff.address.slice(0, 26)}...</strong>
                    <p style={{ fontSize: "12px", color: "var(--text-muted)", marginTop: "2px" }}>
                      {t.frequency === "daily" ? "Every day" : `Every ${t.weekdays.map(d => WEEKDAY_LABELS[d]).join(", ")}`} at {t.timeOfDay}
                      {t.endDate ? ` · until ${new Date(t.endDate).toLocaleDateString()}` : ""}
                    </p>
                    {t.lastRunDate && (
                      <p style={{ fontSize: "12px", color: "var(--text-muted)", marginTop: "2px" }}>Last fired: {t.lastRunDate}</p>
                    )}
                    {t.lastRunError && (
                      <p style={{ fontSize: "12px", color: "var(--danger-hover)", marginTop: "2px" }}>⚠️ Last attempt failed: {t.lastRunError}</p>
                    )}
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <span className={`status-badge ${t.status === "active" ? "success" : t.status === "paused" ? "warning" : "danger"}`}>
                      {t.status.toUpperCase()}
                    </span>
                  </div>
                </div>
                <div style={{ display: "flex", gap: "8px", marginTop: "10px" }}>
                  {t.status !== "cancelled" && (
                    <button
                      className="secondary"
                      style={{ width: "auto", padding: "6px 14px", fontSize: "12px", marginTop: 0 }}
                      disabled={recurringBusyId === t._id}
                      onClick={() => handleToggleRecurringStatus(t._id, t.status === "active" ? "paused" : "active")}
                    >
                      {t.status === "active" ? "⏸ Pause" : "▶ Resume"}
                    </button>
                  )}
                  {t.status !== "cancelled" && (
                    <button
                      className="secondary"
                      style={{ width: "auto", padding: "6px 14px", fontSize: "12px", marginTop: 0, color: "var(--danger-hover)" }}
                      disabled={recurringBusyId === t._id}
                      onClick={() => handleToggleRecurringStatus(t._id, "cancelled")}
                    >
                      ✕ Cancel
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {b2bApproved && (
        <>
          {/* API KEY & BUSINESS PROFILE — B2B/Partner only. Corporate accounts
              don't integrate programmatically, so none of this (key, docs,
              webhook, bulk submit, request log) applies to them. */}
          <form onSubmit={handleGenerateKey} className="delivery-card" style={{ marginBottom: "24px" }}>
            <h3>B2B API Access & Credentials</h3>

            <div className="form-field">
              <label>Company Name</label>
              <input type="text" placeholder="e.g. Acme Logistics Ltd" value={companyName} onChange={e => setCompanyName(e.target.value)} />
            </div>

            <div className="form-field">
              <label>Billing Terms</label>
              <select value={billingTerms} onChange={e => setBillingTerms(e.target.value)}>
                <option value="per_order">Per-Order Payment (Wallet/Card)</option>
                <option value="invoiced">Consolidated Monthly Invoice (Net 14)</option>
              </select>
            </div>

            {apiKey && (
              <div className="form-field">
                <label>Your Live API Key</label>
                <div style={{ position: "relative" }}>
                  <input
                    type="text"
                    readOnly
                    value={showApiKey ? apiKey : apiKey.slice(0, 8) + "•".repeat(Math.max(apiKey.length - 8, 0))}
                    style={{ fontFamily: "monospace", background: "#1e293b", color: "#38bdf8", paddingRight: "76px" }}
                  />
                  <div style={{ position: "absolute", right: "8px", top: "50%", transform: "translateY(-50%)", display: "flex", gap: "4px" }}>
                    <button
                      type="button"
                      className="secondary"
                      onClick={() => setShowApiKey(v => !v)}
                      title={showApiKey ? "Hide API key" : "View API key"}
                      style={{ width: "auto", margin: 0, padding: "4px 8px", fontSize: "14px" }}
                    >
                      {showApiKey ? "🙈" : "👁️"}
                    </button>
                    {showApiKey && (
                      <button
                        type="button"
                        className="secondary"
                        onClick={handleCopyApiKey}
                        title="Copy API key"
                        style={{ width: "auto", margin: 0, padding: "4px 8px", fontSize: "14px" }}
                      >
                        {apiKeyCopied ? "✅" : "📋"}
                      </button>
                    )}
                  </div>
                </div>
                {apiKeyCopied && <p style={{ color: "var(--success)", fontSize: "12px", marginTop: "4px" }}>Copied to clipboard!</p>}
              </div>
            )}

            {error && <p className="error">{error}</p>}
            {message && <p style={{ color: "var(--success)", fontSize: "14px", marginBottom: "8px" }}>{message}</p>}

            <button type="submit" disabled={busy}>
              {apiKey ? "Regenerate API Key" : "Issue API Key"}
            </button>

            {/* API DOCUMENTATION — how to actually use the key/endpoints above */}
            <Collapsible title="📖 API Documentation — Authentication, Endpoints & Webhook">
              <p className="hint" style={{ marginBottom: "10px" }}>
                Base URL for all requests below:
              </p>
              <CodeBlock>{API_BASE_URL}</CodeBlock>

              <h4 style={{ marginTop: "16px" }}>1. Authentication</h4>
              <p className="hint" style={{ marginBottom: "8px" }}>
                Send your API key on every programmatic request in an <code>X-API-Key</code> header
                (a <code>Bearer</code> Authorization header also works). The key only works while your
                account's B2B status is <strong>approved</strong> — a revoked or pending account gets a 401.
                Regenerating your key above immediately invalidates the old one.
              </p>
              <CodeBlock>{`X-API-Key: ${displayApiKey}`}</CodeBlock>

              <h4 style={{ marginTop: "16px" }}>2. Submit Single Order — POST /api/v1/b2b/orders</h4>
              <p className="hint" style={{ marginBottom: "8px" }}>
                Submit a single delivery order directly. The system calculates the quote in real-time, dispatches the order to
                nearby dispatchers, generates a zero-login customer tracking link (<code>shareUrl</code>), and immediately returns the
                confirmed delivery <code>price</code> and order details.
              </p>
              <CodeBlock>{`curl -X POST ${API_BASE_URL}/api/v1/b2b/orders \\
  -H "X-API-Key: ${displayApiKey}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "pickup": { "address": "ShopRite, Ikeja City Mall, Lagos", "lat": 6.6018, "lng": 3.3514 },
    "dropoff": { "address": "12 Admiralty Way, Lekki Phase 1, Lagos", "lat": 6.4474, "lng": 3.4723 },
    "dropoffContact": { "name": "Jane Doe", "phone": "08012345678" },
    "pickupContact": { "name": "Store Branch #1", "phone": "08099990000" },
    "vehicleType": "bike",
    "packageType": "bike_under_5kg",
    "packageCategory": "food_consumables",
    "packageDescription": "Order #SP-10234 grocery bag",
    "externalReference": "SP-10234",
    "isInsured": false,
    "city": "Lagos"
  }'`}</CodeBlock>
              <p className="hint" style={{ margin: "8px 0" }}>
                <code>pickup</code> and <code>dropoff</code> (<code>address</code>, <code>lat</code>, <code>lng</code>) are required.
                <code>externalReference</code> allows you to attach your own system's order or transaction ID.
              </p>
              <p className="hint" style={{ marginBottom: "8px" }}>Response (HTTP 201) — returns confirmed delivery price and live customer tracking link:</p>
              <CodeBlock>{`{
  "status": "created",
  "orderId": "66d5b0a1f9c8...",
  "price": 1450,
  "total": 1450,
  "deliveryFee": 1450,
  "priceSnapshot": {
    "baseFare": 800,
    "distanceKm": 5.2,
    "distanceFare": 650,
    "total": 1450
  },
  "riderOffered": true,
  "publicShareToken": "4e7c8b91a2...",
  "shareUrl": "${API_BASE_URL.replace('/api/v1', '')}/share/4e7c8b91a2...",
  "deliveryToken": "482910",
  "externalReference": "SP-10234"
}`}</CodeBlock>

              <h4 style={{ marginTop: "16px" }}>3. How to Use the Customer Share Link</h4>
              <p className="hint" style={{ marginBottom: "8px" }}>
                Every B2B delivery automatically generates a public, zero-login customer share link (<code>shareUrl</code>).
                This link allows your end-customers or recipients to view and track their delivery without needing a FastSend account:
              </p>
              <ul className="hint" style={{ paddingLeft: "20px", marginBottom: "8px", lineHeight: "1.6" }}>
                <li><strong>Real-time Tracking:</strong> Live status progression (<code>requested → accepted → arrived_at_pickup → in_transit → delivered</code>).</li>
                <li><strong>Dispatcher Info:</strong> Rider's name, phone number, and vehicle type once assigned.</li>
                <li><strong>Delivery Confirmation PIN:</strong> Displays the 6-digit PIN (<code>deliveryToken</code>) the recipient shows the rider to confirm handoff.</li>
                <li><strong>SMS / WhatsApp Notifications:</strong> Send the <code>shareUrl</code> directly to your buyer in order confirmation messages (e.g. <em>"Track your delivery in real time: [shareUrl]"</em>).</li>
                <li><strong>Webview / Iframe:</strong> Embed the <code>shareUrl</code> in your mobile app or web portal order history screen.</li>
              </ul>
              <p className="hint" style={{ marginBottom: "8px" }}>
                You can also retrieve or regenerate the share link at any time via <code>POST /api/v1/b2b/orders/:id/share-link</code>:
              </p>
              <CodeBlock>{`curl -X POST ${API_BASE_URL}/api/v1/b2b/orders/<orderId>/share-link \\
  -H "X-API-Key: ${displayApiKey}"`}</CodeBlock>

              <h4 style={{ marginTop: "16px" }}>4. Submit Batch Orders — POST /api/v1/b2b/orders/bulk</h4>
              <p className="hint" style={{ marginBottom: "8px" }}>
                Submit multiple delivery orders in a single call. Each order is priced independently, generates its own
                <code>shareUrl</code> and <code>price</code>, and is offered to dispatchers:
              </p>
              <CodeBlock>{`curl -X POST ${API_BASE_URL}/api/v1/b2b/orders/bulk \\
  -H "X-API-Key: ${displayApiKey}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "orders": [
      {
        "pickup": { "address": "Ikeja, Lagos", "lat": 6.6018, "lng": 3.3514 },
        "dropoff": { "address": "Lekki Phase 1, Lagos", "lat": 6.4474, "lng": 3.4723 },
        "dropoffContact": { "name": "Jane Doe", "phone": "08012345678" },
        "externalReference": "BATCH-001"
      }
    ]
  }'`}</CodeBlock>
              <p className="hint" style={{ marginBottom: "8px" }}>Response — returns individual status, price, and share URL for each item:</p>
              <CodeBlock>{`{
  "totalSubmitted": 1,
  "successful": 1,
  "failed": 0,
  "results": [
    {
      "index": 0,
      "status": "created",
      "orderId": "...",
      "riderOffered": true,
      "price": 1450,
      "total": 1450,
      "shareUrl": "https://fastsend.ng/share/4e7c8b91a2...",
      "externalReference": "BATCH-001"
    }
  ]
}`}</CodeBlock>

              <h4 style={{ marginTop: "16px" }}>5. Order Status Webhook</h4>
              <p className="hint" style={{ marginBottom: "8px" }}>
                If you've saved a Webhook URL below, we POST this payload to it every time one of your
                orders changes status — no polling required. Includes your original <code>externalReference</code> and customer <code>shareUrl</code>:
              </p>
              <CodeBlock>{`POST <your webhook URL>
{
  "event": "order.status_changed",
  "orderId": "6a8f...",
  "status": "in_transit",
  "externalReference": "SP-10234",
  "shareUrl": "https://fastsend.ng/share/4e7c8b91a2...",
  "timestamp": "2026-08-22T10:00:00.000Z"
}`}</CodeBlock>

              <h4 style={{ marginTop: "16px" }}>6. Check Order Status — GET /api/v1/b2b/orders/:id</h4>
              <p className="hint" style={{ marginBottom: "8px" }}>
                Poll this endpoint with the <code>orderId</code> to check real-time status, confirmed delivery fee (<code>price</code> / <code>total</code>),
                dispatcher location, and <code>shareUrl</code>. Scoped to your own orders only.
              </p>
              <CodeBlock>{`curl ${API_BASE_URL}/api/v1/b2b/orders/<orderId> \\
  -H "X-API-Key: ${displayApiKey}"`}</CodeBlock>
              <CodeBlock>{`{
  "_id": "6a8f...",
  "status": "in_transit",
  "paymentStatus": "paid",
  "paymentMethod": "b2b_invoice",
  "price": 1450,
  "total": 1450,
  "shareUrl": "https://fastsend.ng/share/4e7c8b91a2...",
  "pickup": { "address": "Ikeja, Lagos" },
  "dropoff": { "address": "Lekki Phase 1, Lagos" },
  "priceSnapshot": { "total": 1450 },
  "rider": { "name": "Chidi", "phone": "08033334444", "vehicleType": "bike" },
  "statusHistory": [ { "status": "requested", "at": "..." }, ... ],
  "createdAt": "...",
  "updatedAt": "..."
}`}</CodeBlock>

              <h4 style={{ marginTop: "16px" }}>7. Tracking Your Requests</h4>
              <p className="hint">
                Every request authenticated with your API key — success or failure — is recorded and shown
                in the "API Request Log" section below, so you can confirm your integration's calls actually
                reached us without needing access to our server logs.
              </p>
            </Collapsible>
          </form>

          {/* WEBHOOK — for integrations like StorePointe to receive order status callbacks */}
          <form onSubmit={handleSaveWebhook} className="delivery-card" style={{ marginBottom: "24px" }}>
            <h3>Order Status Webhook</h3>
            <p className="hint">
              For integrations (e.g. a StorePointe supermarket checkout) that need live order status —
              we'll POST <code>order.status_changed</code> events here as your orders progress.
            </p>
            <div className="form-field">
              <label>Webhook URL</label>
              <input type="url" placeholder="https://your-app.example.com/webhooks/fastsend" value={webhookUrl} onChange={e => setWebhookUrl(e.target.value)} />
            </div>
            <button type="submit" disabled={webhookBusy} className="secondary">
              {webhookBusy ? "Saving..." : "Save Webhook URL"}
            </button>
          </form>

          {/* BULK ORDER DISPATCH */}
          <div className="delivery-card" style={{ marginBottom: "24px" }}>
            <h3>Programmatic Bulk Order Submission</h3>
            <p className="hint">Submit JSON batch array to dispatch multiple orders at once.</p>

            <textarea
              rows={8}
              value={bulkInput}
              onChange={e => setBulkInput(e.target.value)}
              style={{ fontFamily: "monospace", width: "100%", padding: "12px", background: "var(--bg-input)", color: "var(--text-primary)", borderRadius: "var(--radius-sm)", border: "1px solid var(--border)", marginBottom: "12px" }}
            />

            <button disabled={busy} onClick={handleBulkSubmit}>
              {busy ? "Processing Batch..." : "Submit Batch Orders"}
            </button>

            {bulkResults && (
              <div className="quote-box" style={{ marginTop: "16px" }}>
                <h4>Batch Dispatch Results ({bulkResults.successful}/{bulkResults.totalSubmitted} Success)</h4>
                {bulkResults.results.map((res, i) => (
                  <div key={i} className="info-row" style={{ fontSize: "13px" }}>
                    <span>Order #{res.index + 1}</span>
                    <span style={{ color: res.status === "created" ? "var(--success)" : "var(--danger-hover)" }}>
                      {res.status === "created" ? `✓ Created (₦${res.total})` : `✕ Failed: ${res.error}`}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
        )}

          {/* ORDERS / ORDERS INITIATED — for B2B/Partner accounts, strictly
              orders submitted via the bulk API (this list and the regular
              customer "My Orders" page partition those cleanly — an order
              shows on exactly one of the two, never both). For Corporate
              accounts, every order they've placed through the app, since
              that's their only ordering path and this is their one view of
              what will land on an invoice. */}
          <Collapsible
            title={corporateApproved ? "📦 Your Orders" : "📦 Orders Initiated"}
            className="delivery-card" defaultOpen style={{ marginBottom: "24px" }}
          >
            <p className="hint" style={{ marginBottom: "12px" }}>
              {corporateApproved
                ? "Your most recent 100 orders, newest first — status updates live as riders progress each delivery. Every order here is billed to your consolidated invoice below."
                : "Your most recent 100 orders submitted via the API, newest first — status updates live as riders progress each delivery. Orders placed through the regular order form appear on \"My Orders\" instead."}
            </p>

            <div className="role-tabs" style={{ marginBottom: "12px" }}>
              <button className={`role-tab ${orderFilter === "all" ? "active" : ""}`} onClick={() => setOrderFilter("all")}>
                All ({myOrders.length})
              </button>
              <button className={`role-tab ${orderFilter === "active" ? "active" : ""}`} onClick={() => setOrderFilter("active")}>
                🔴 Live ({orderActiveCount})
              </button>
              <button className={`role-tab ${orderFilter === "completed" ? "active" : ""}`} onClick={() => setOrderFilter("completed")}>
                Completed
              </button>
              <button className={`role-tab ${orderFilter === "cancelled" ? "active" : ""}`} onClick={() => setOrderFilter("cancelled")}>
                Cancelled
              </button>
            </div>

            <input
              type="text"
              placeholder="Search by order ID, address, or dispatcher name..."
              value={orderSearch}
              onChange={e => setOrderSearch(e.target.value)}
              style={{ marginBottom: "12px" }}
            />

            {filteredOrders.length === 0 ? (
              <p className="hint">
                {myOrders.length === 0
                  ? (corporateApproved
                      ? "No orders yet — place one from \"Send Package\" to see it here."
                      : "No API-submitted orders yet — submit one via the API above to see it here.")
                  : "No orders match this filter/search."}
              </p>
            ) : (
              filteredOrders.map(o => (
                <div key={o._id} className="history-row">
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div>
                      <strong>Order #{o._id.slice(-6).toUpperCase()}</strong>
                      <p style={{ fontSize: "12px", color: "var(--text-muted)", marginTop: "2px" }}>
                        {o.pickup?.address?.slice(0, 28)}... → {o.dropoff?.address?.slice(0, 28)}...
                      </p>
                      <p style={{ fontSize: "12px", color: "var(--text-muted)", marginTop: "2px" }}>
                        {new Date(o.createdAt).toLocaleString()}{o.rider ? ` · ${o.rider.name}` : ""}
                      </p>
                      {o.deliveryToken?.code && !["cancelled", "failed"].includes(o.status) && (
                        <p style={{ fontSize: "12px", color: "var(--text-muted)", marginTop: "2px" }}>
                          🔑 Verification code: <strong style={{ letterSpacing: "2px", color: "var(--text-secondary)" }}>{o.deliveryToken.code}</strong>
                        </p>
                      )}
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <strong style={{ fontSize: "16px" }}>₦{(o.priceSnapshot?.total || 0).toLocaleString()}</strong>
                      <span className={`status-badge ${ORDER_STATUS_BADGE[o.status] || ""}`} style={{ marginLeft: "8px" }}>
                        {o.status.replace(/_/g, " ").toUpperCase()}
                      </span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </Collapsible>

        {b2bApproved && (
        <>

          {/* API REQUEST LOG — every request made with this account's API
              key, tracked here so it's never something you can only see by
              asking us to check server logs. */}
          <div className="delivery-card" style={{ marginBottom: "24px" }}>
            <h3>📊 API Request Log</h3>
            <p className="hint" style={{ marginBottom: "12px" }}>
              Every request made with your API key — this shows the last 100.
            </p>
            {!apiKey ? (
              <p className="hint">Issue an API key above to start tracking requests.</p>
            ) : apiLogs.length === 0 ? (
              <p className="hint">No API requests yet — this fills in as your integration calls the endpoints below.</p>
            ) : (
              apiLogs.map(log => (
                <div key={log._id} className="info-row" style={{ fontSize: "13px" }}>
                  <span>
                    <strong>{log.method}</strong> {log.endpoint}
                    <span style={{ color: "var(--text-muted)", marginLeft: "8px" }}>
                      {new Date(log.createdAt).toLocaleString()}
                    </span>
                  </span>
                  <span style={{ color: log.statusCode < 300 ? "var(--success)" : "var(--danger-hover)", textAlign: "right" }}>
                    {log.statusCode}{log.summary ? ` — ${log.summary}` : ""}
                  </span>
                </div>
              ))
            )}
          </div>
        </>
        )}

          {/* CONSOLIDATED INVOICES — shown for both B2B and Corporate; only
              meaningful once billingTerms is "invoiced" (gated inline below). */}
          <div className="delivery-card">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
              <h3>Consolidated Invoices</h3>
              {billingTerms === "invoiced" && (
                <button className="secondary" style={{ width: "auto", margin: 0 }} disabled={busy} onClick={handleGenerateInvoice}>
                  + Generate New Invoice
                </button>
              )}
            </div>

            {billingTerms === "invoiced" && (
              <div className="quote-box" style={{ marginBottom: "12px", background: "rgba(255,255,255,0.03)" }}>
                <span className="label" style={{ fontSize: "11px", color: "var(--text-muted)", display: "block" }}>Total Outstanding Balance</span>
                <strong style={{ fontSize: "22px", color: totalOwed > 0 ? "var(--warning-hover)" : "var(--success-hover)" }}>
                  ₦{totalOwed.toLocaleString()}
                </strong>
                {totalOwed === 0 && <span style={{ marginLeft: "8px", fontSize: "13px", color: "var(--text-muted)" }}>You're all paid up.</span>}
              </div>
            )}

            {billingMode === "consolidated" && billingTerms === "invoiced" && (
              <p className="hint" style={{ marginBottom: "12px" }}>
                Consolidated billing: any invoice still unpaid when you generate the next one is automatically
                folded into it. Pay an invoice any time before that happens to keep it separate.
              </p>
            )}

            {invoices.length === 0 ? (
              <p className="hint">No consolidated invoices yet.</p>
            ) : (
              invoices.map(inv => {
                const badge = INVOICE_STATUS_BADGE[inv.status] || INVOICE_STATUS_BADGE.unpaid;
                const expanded = expandedInvoiceId === inv._id;
                return (
                  <div key={inv._id} className="history-row">
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div>
                        <strong>Invoice #{inv._id.slice(-6).toUpperCase()}</strong>
                        <p style={{ fontSize: "12px", color: "var(--text-muted)", marginTop: "2px" }}>
                          {inv.orders.length} deliveries · Due: {new Date(inv.dueDate).toLocaleDateString()}
                        </p>
                        {inv.status === "combined" && inv.combinedInto && (
                          <p style={{ fontSize: "12px", color: "var(--text-muted)", marginTop: "2px" }}>
                            Combined into Invoice #{inv.combinedInto.toString().slice(-6).toUpperCase()}
                          </p>
                        )}
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <strong style={{ fontSize: "16px" }}>₦{inv.totalAmount.toLocaleString()}</strong>
                        <span className={`status-badge ${badge.cls}`} style={{ marginLeft: "8px" }}>
                          {badge.label}
                        </span>
                      </div>
                    </div>

                    <div style={{ display: "flex", gap: "8px", marginTop: "10px", flexWrap: "wrap", alignItems: "center" }}>
                      <button
                        className="secondary"
                        style={{ width: "auto", padding: "6px 14px", fontSize: "12px", marginTop: 0 }}
                        onClick={() => setExpandedInvoiceId(expanded ? null : inv._id)}
                      >
                        {expanded ? "Hide Details" : "🔎 View"}
                      </button>

                      {inv.status === "unpaid" && (
                        <>
                          <select
                            value={payMethodMap[inv._id] || "wallet"}
                            onChange={e => setPayMethodMap(m => ({ ...m, [inv._id]: e.target.value }))}
                            style={{ width: "auto", margin: 0, padding: "6px 10px", fontSize: "12px" }}
                          >
                            <option value="wallet">Pay from Wallet</option>
                            <option value="paystack">Pay with Paystack</option>
                          </select>
                          <button
                            style={{ width: "auto", padding: "6px 14px", fontSize: "12px", marginTop: 0 }}
                            disabled={payBusyId === inv._id}
                            onClick={() => handlePayInvoice(inv._id)}
                          >
                            {payBusyId === inv._id ? "Processing..." : "💳 Pay Now"}
                          </button>
                        </>
                      )}
                    </div>

                    {expanded && (
                      <div className="quote-box" style={{ marginTop: "10px" }}>
                        {inv.orders.map(o => (
                          <div className="info-row" key={o._id}>
                            <span className="label">{o.pickup?.address?.slice(0, 24)}... → {o.dropoff?.address?.slice(0, 24)}...</span>
                            <span className="value">₦{(o.priceSnapshot?.total || 0).toLocaleString()}</span>
                          </div>
                        ))}
                        {inv.paidAt && (
                          <div className="info-row">
                            <span className="label">Paid</span>
                            <span className="value">{new Date(inv.paidAt).toLocaleString()} via {inv.paymentMethod}{inv.paidBy === "admin" ? " (admin)" : ""}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </>
      )}
      </div>
    </div>
  </div>
);
}
